// ============================================================================
// Quotezilla hand-off — a new brief that arrives with the job already in it
// ============================================================================
// Quotezilla, Fusion's quotation system, raises supplier briefs against a
// project and sends whoever raised it here, to /briefs/new, with the job in the
// query string. Its parameters are named after THIS form's own fields, so
// reading them is a straight copy rather than a translation table that drifts
// the first time either side renames something.
//
// Deliberately a WHITELIST. A link is something anyone can edit and forward, so
// only the plain facts of the job come from it. Everything that decides who can
// see a brief or what suppliers are shown — is_privileged, nda_required, the
// show_* flags, the recipient list — stays a decision made on this page.
//
// Two shapes disagree, and are converted HERE rather than at the Quotezilla end,
// because they are facts about how this form stores things:
//   response deadline   a moment here, a date over there → end of the working day
//   brief content       rich text here, plain text over there → escaped paragraphs
//
// Nothing is saved as a result of any of this: the staffer still has a form in
// front of them, and still picks the suppliers.
// ============================================================================

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

// Quotezilla knows the date a response is wanted, not the hour. End of the
// working day is the reading that can only be too generous, never too tight —
// and the staffer can move it before anything goes out.
export const DEFAULT_DEADLINE_TIME = '17:00';

const LIMITS = { text: 200, content: 20000, suppliers: 400, id: 64 };

const clean = (value, max) => (typeof value === 'string' ? value.trim().slice(0, max) : '');

// A date we don't recognise is dropped rather than guessed at: an empty picker
// says "fill me in", a wrong date says nothing at all.
const dateOnly = (value) => (DATE_ONLY.test(clean(value, 10)) ? clean(value, 10) : '');

export function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// The requirements arrive as typed text. The editor here holds HTML, so blank
// lines become paragraph breaks and everything else is escaped — a supplier
// brief is not the place to let a query string inject markup.
export function plainTextToHtml(text) {
  const lines = String(text || '').split(/\r?\n/).map((l) => l.trim());
  const paragraphs = lines.filter(Boolean).map((l) => `<p>${escapeHtml(l)}</p>`);
  return paragraphs.join('');
}

/**
 * Read a hand-off out of the query string.
 *
 * @param {string} search  window.location.search, or anything URLSearchParams takes
 * @returns {{values: object, from: object}|null}  null when this isn't a hand-off,
 *          so the form behaves exactly as it always has for a brief started here.
 */
export function quotezillaPrefill(search) {
  const q = new URLSearchParams(search || '');
  if (q.get('source') !== 'quotezilla') return null;

  const values = {};
  const put = (key, value) => { if (value) values[key] = value; };

  put('fusion_project_number', clean(q.get('fusion_project_number'), LIMITS.text));
  put('fusion_project_name', clean(q.get('fusion_project_name'), LIMITS.text));
  put('client', clean(q.get('client'), LIMITS.text));
  put('brief_name', clean(q.get('brief_name'), LIMITS.text));
  put('brief_content', plainTextToHtml(clean(q.get('brief_content'), LIMITS.content)));

  const deadline = dateOnly(q.get('required_response_time'));
  put('required_response_time', deadline ? `${deadline}T${DEFAULT_DEADLINE_TIME}` : '');
  put('event_live_date', dateOnly(q.get('event_live_date')));
  put('event_live_end_date', dateOnly(q.get('event_live_end_date')));

  return {
    values,
    from: {
      projectNumber: clean(q.get('fusion_project_number'), LIMITS.text),
      projectName: clean(q.get('fusion_project_name'), LIMITS.text),
      client: clean(q.get('client'), LIMITS.text),
      // Names typed in Quotezilla. They are a hint for whoever picks recipients
      // here — suppliers are chosen from our own list, never from a link.
      suggestedSuppliers: clean(q.get('suggested_suppliers'), LIMITS.suppliers),
      briefId: clean(q.get('quotezilla_brief_id'), LIMITS.id),
      projectId: clean(q.get('quotezilla_project_id'), LIMITS.id),
    },
  };
}

/** What the banner says: where it came from, and what still needs a human. */
export function handoffNote(from) {
  const job = [from?.projectNumber, from?.client].filter(Boolean).join(' · ');
  const parts = [job ? `Details came from Quotezilla (${job}).` : 'Details came from Quotezilla.'];
  parts.push('Check them, then pick the suppliers as usual — nothing has been saved or sent yet.');
  if (from?.suggestedSuppliers) parts.push(`Quotezilla suggested: ${from.suggestedSuppliers}.`);
  return parts.join(' ');
}
